import { useEffect, useState, useCallback } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { TenantBadge } from '@/components/TenantIdentity'
import { getAdminEmail, logoutAdmin } from '@/utils/adminAuth'
import { getAdminPostLogoutPath } from '@/config/tenant'
import {
  getNotificacoesAdmin,
  getNotificacoesAdminCount,
  markNotificacaoAdminLida,
  markAllNotificacoesAdminLidas,
  type NotificacaoAdminResumo,
} from '@/api/adminApi'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { loadAndApplyTheme } from '@/utils/theme'
import { ADMIN_SUPPORT_TICKETS_URL } from '@/config/support'
import styles from './Admin.module.css'

const LINK_GROUPS = [
  {
    title: 'Base do app',
    links: [
      { to: '/admin/dashboard', label: 'Início' },
      { to: '/admin/menu', label: 'Menu / Banner' },
      { to: '/admin/identidade', label: 'Identidade da empresa' },
      { to: '/admin/qr', label: 'QR Code & link' },
      { to: '/admin/visibilidade', label: 'Visibilidade / Fluxos' },
      { to: '/admin/tema', label: 'Tema / Layout' },
      { to: '/admin/conta', label: 'Minha conta / Senha' },
    ],
  },
  {
    title: 'Conteúdo público',
    links: [
      { to: '/admin/quem-somos', label: 'Quem somos' },
      { to: '/admin/servicos', label: 'Serviços' },
      { to: '/admin/categorias', label: 'Categorias' },
      { to: '/admin/contatos', label: 'Contatos' },
      { to: '/admin/mapa', label: 'Mapa' },
      { to: '/admin/galeria', label: 'Galeria' },
      { to: '/admin/vip', label: 'VIP' },
    ],
  },
  {
    title: 'Operação',
    links: [
      { to: '/admin/unidades', label: 'Unidades' },
      { to: '/admin/profissionais', label: 'Profissionais' },
      { to: '/admin/disponibilidade', label: 'Horários' },
      { to: '/admin/agendamentos', label: 'Agendamentos' },
      { to: '/admin/pacotes', label: 'Pacotes' },
      { to: '/admin/tarefas', label: 'Tarefas' },
    ],
  },
  {
    title: 'Comunidade e moderação',
    links: [
      { to: '/admin/feedbacks', label: 'Feedbacks' },
      { to: '/admin/social', label: 'Feed social' },
    ],
  },
] as const

const ROUTE_TITLES: Record<string, string> = {
  '/admin/dashboard': 'Visão geral',
  '/admin/menu': 'Menu / Banner',
  '/admin/identidade': 'Identidade da empresa',
  '/admin/qr': 'QR Code & link',
  '/admin/visibilidade': 'Visibilidade / Fluxos',
  '/admin/quem-somos': 'Quem somos',
  '/admin/servicos': 'Serviços',
  '/admin/tema': 'Tema / Layout',
  '/admin/categorias': 'Categorias',
  '/admin/contatos': 'Contatos',
  '/admin/mapa': 'Mapa',
  '/admin/galeria': 'Galeria',
  '/admin/unidades': 'Unidades',
  '/admin/profissionais': 'Profissionais',
  '/admin/disponibilidade': 'Horários',
  '/admin/agendamentos': 'Agendamentos',
  '/admin/pacotes': 'Pacotes',
  '/admin/tarefas': 'Tarefas',
  '/admin/feedbacks': 'Feedbacks',
  '/admin/social': 'Feed social',
  '/admin/vip': 'VIP',
  '/admin/conta': 'Minha conta',
}

function notificacaoDestino(n: NotificacaoAdminResumo): string {
  if (n.agendamento_id) return '/admin/agendamentos'
  if (n.tipo.includes('feedback')) return '/admin/feedbacks'
  return '/admin/social'
}

export function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [notifCount, setNotifCount] = useState(0)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifs, setNotifs] = useState<NotificacaoAdminResumo[]>([])
  const [markingAll, setMarkingAll] = useState(false)
  const currentTitle = ROUTE_TITLES[location.pathname] ?? 'Admin'
  const { notifyInfo } = useSaveFeedback()

  const refreshNotifs = useCallback(async () => {
    const email = getAdminEmail()
    if (!email) return
    try {
      const [count, list] = await Promise.all([
        getNotificacoesAdminCount(email),
        getNotificacoesAdmin(email, 10),
      ])
      setNotifCount(count)
      setNotifs(list)
    } catch {
      setNotifCount(0)
      setNotifs([])
    }
  }, [])

  useEffect(() => {
    refreshNotifs()
  }, [refreshNotifs])

  useEffect(() => {
    if (location.pathname === '/admin/tema') return
    void loadAndApplyTheme()
  }, [location.pathname])

  useEffect(() => {
    return () => {
      void loadAndApplyTheme()
    }
  }, [])

  useEffect(() => {
    if (notifOpen) refreshNotifs()
  }, [notifOpen, refreshNotifs])

  const handleNotifClick = async (n: NotificacaoAdminResumo) => {
    try {
      if (!n.lida) {
        await markNotificacaoAdminLida(n.id)
      }
      setNotifOpen(false)
      await refreshNotifs()
      navigate(notificacaoDestino(n))
    } catch {
      navigate(notificacaoDestino(n))
    }
  }

  const handleMarkAll = async () => {
    setMarkingAll(true)
    try {
      await markAllNotificacoesAdminLidas()
      await refreshNotifs()
    } finally {
      setMarkingAll(false)
    }
  }

  const handleSair = () => {
    const destino = getAdminPostLogoutPath()
    logoutAdmin()
    notifyInfo('Sessão de administrador terminada.')
    window.location.replace(destino)
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!sidebarOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [sidebarOpen])

  return (
    <div className={`${styles.layout} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.menuToggle}
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label={sidebarOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={sidebarOpen}
            aria-controls="admin-sidebar-nav"
          >
            <span className={styles.menuToggleIcon} aria-hidden>
              {sidebarOpen ? '✕' : '☰'}
            </span>
          </button>
          <div className={styles.headerTitleBlock}>
            <span className={styles.headerTitle}>Painel admin</span>
            <span className={styles.headerSubtitle}>{currentTitle}</span>
          </div>
        </div>
        <div className={styles.adminHeaderActions}>
          <a
            href={ADMIN_SUPPORT_TICKETS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.headerSupportBtn}
          >
            Suporte
          </a>
          <button
            type="button"
            className={styles.adminNotifBtn}
            onClick={() => setNotifOpen((v) => !v)}
            aria-label="Notificações admin"
            aria-expanded={notifOpen}
          >
            🔔
            {notifCount > 0 && (
              <span className={styles.adminNotifBadge}>{notifCount > 9 ? '9+' : notifCount}</span>
            )}
          </button>
          {notifOpen && (
            <div className={styles.adminNotifPanel}>
              <div className={styles.adminNotifPanelHeader}>
                <div className={styles.adminNotifPanelTitle}>Notificações</div>
                {notifs.some((n) => !n.lida) && (
                  <button
                    type="button"
                    className={styles.adminNotifMarkAll}
                    onClick={handleMarkAll}
                    disabled={markingAll}
                  >
                    {markingAll ? '…' : 'Marcar todas lidas'}
                  </button>
                )}
              </div>
              {notifs.length === 0 ? (
                <p className={styles.placeholderMsg}>Sem notificações.</p>
              ) : (
                <div className={styles.adminNotifList}>
                  {notifs.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className={`${styles.adminNotifItem} ${!n.lida ? styles.adminNotifItemUnread : ''}`}
                      onClick={() => handleNotifClick(n)}
                    >
                      <div className={styles.adminNotifItemTitle}>{n.titulo}</div>
                      <div className={styles.adminNotifItemMsg}>{n.mensagem}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button type="button" className={styles.btnSairAdmin} onClick={handleSair}>
            Sair do admin
          </button>
        </div>
      </header>
      <button
        type="button"
        className={styles.sidebarBackdrop}
        aria-label="Fechar menu"
        tabIndex={sidebarOpen ? 0 : -1}
        onClick={() => setSidebarOpen(false)}
      />
      <div className={styles.contentArea}>
        <aside className={styles.sidebar} id="admin-sidebar-nav">
          <div className={styles.sidebarBrand}>
            <div className={styles.sidebarBrandTitle}>Área restrita</div>
            <div className={styles.sidebarBrandSub}>Painel admin</div>
            <div className={styles.sidebarTenant}>
              <TenantBadge showSlug />
            </div>
          </div>
          <nav className={styles.sidebarNav}>
            {LINK_GROUPS.map((group) => (
              <div key={group.title} className={styles.sidebarGroup}>
                <div className={styles.sidebarGroupTitle}>{group.title}</div>
                {group.links.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `${styles.sidebarLink} ${isActive ? styles.active : ''}`
                    }
                    end={to === '/admin/dashboard'}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <div className={styles.sidebarFooter}>
            <a
              href={ADMIN_SUPPORT_TICKETS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.sidebarSupportBtn}
              onClick={() => setSidebarOpen(false)}
            >
              <span className={styles.sidebarSupportIcon} aria-hidden>
                💬
              </span>
              <span className={styles.sidebarSupportText}>
                <span className={styles.sidebarSupportLabel}>Suporte</span>
                <span className={styles.sidebarSupportHint}>Abrir pedido de ajuda</span>
              </span>
            </a>
          </div>
        </aside>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
